import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, Switch } from '@mui/material';
import toast from 'react-hot-toast';
import useHttp from '../../../../hooks/use-http';
import MuiDialog from '../../../UI/MuiDialog/MuiDialog';
import OButton from '../../../UI/FormControls/OButton';
import { colors } from '../../../../../shared/colors';
import { icons } from '../../../../../shared/icons';
import { handleErrorMessage } from '../../../../../shared/api/handleErrorMessage';
import { urls } from './api/urls';
import {
  formatDatum,
  generisiIzvestajProjektaPDF,
  pripremiIzvestajIzOdgovora,
} from './generisiIzvestajProjektaPDF';
import IzmenaIzvestajaProjektaDialog from './IzmenaIzvestajaProjektaDialog';
import styles from './IzvestajiPanel.module.css';

const IzvestajiPanel = ({ idMasine }) => {
  const { t } = useTranslation();
  const { getData } = useHttp('');
  const [izvestaji, setIzvestaji] = useState([]);
  const [izmena, setIzmena] = useState(null);
  const [zaBrisanje, setZaBrisanje] = useState(null);
  const [pregled, setPregled] = useState(null);
  const urlRef = useRef(null);
  const jeInmold = localStorage.idFirme === '1';

  const ucitaj = useCallback(async () => {
    if (!idMasine) {
      setIzvestaji([]);
      return;
    }
    const odgovor = await getData({
      url: urls.VRATI,
      method: 'POST',
      data: { idMasine },
    });
    if (odgovor?.isAxiosError) {
      toast.error(handleErrorMessage(odgovor));
      setIzvestaji([]);
      return;
    }
    setIzvestaji(Array.isArray(odgovor) ? odgovor : []);
  }, [idMasine]);

  useEffect(() => {
    ucitaj();
  }, [ucitaj]);

  const oslobodiUrl = () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  };

  useEffect(() => oslobodiUrl, []);

  const otvoriPregled = async (izvestaj, jezik) => {
    const odgovor = await getData({
      url: urls.VRATI_JEDAN,
      method: 'POST',
      data: { id: izvestaj.id },
    });
    if (odgovor?.isAxiosError) {
      toast.error(handleErrorMessage(odgovor));
      return;
    }

    try {
      const pripremljen = await pripremiIzvestajIzOdgovora(odgovor);
      const { doc } = await generisiIzvestajProjektaPDF({
        izvestaj: pripremljen,
        jezik,
      });
      oslobodiUrl();
      urlRef.current = URL.createObjectURL(doc.output('blob'));
      setPregled({
        url: urlRef.current,
        naslov: `${formatDatum(izvestaj.datumIzvestaja)} · ${jezik}`,
      });
    } catch (error) {
      console.error(error);
      toast.error(t('projectReports.pdfError'));
    }
  };

  const izmeniVidljivost = async (izvestaj, vidljivoKlijentu) => {
    const odgovor = await getData({
      url: urls.VIDLJIVO,
      method: 'POST',
      data: { id: izvestaj.id, vidljivoKlijentu },
    });
    if (odgovor?.isAxiosError) {
      toast.error(handleErrorMessage(odgovor));
      return;
    }
    ucitaj();
  };

  const obrisi = async () => {
    const odgovor = await getData({
      url: urls.OBRISI,
      method: 'POST',
      data: { id: zaBrisanje.id },
    });
    setZaBrisanje(null);
    if (odgovor?.isAxiosError) {
      toast.error(handleErrorMessage(odgovor));
      return;
    }
    ucitaj();
  };

  if (!idMasine) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.zaglavlje}>
        <h3 className={styles.naslov}>{t('projectReports.title')}</h3>
        {jeInmold && (
          <button
            type="button"
            className={styles.dugmeNovi}
            onClick={() => setIzmena({ id: null })}
          >
            <i className="fas fa-plus" /> {t('projectReports.new')}
          </button>
        )}
      </div>

      {izvestaji.length === 0 ? (
        <div className={styles.prazno}>{t('projectReports.empty')}</div>
      ) : (
        <div className={styles.lista}>
          {izvestaji.map((izvestaj) => (
            <div key={izvestaj.id} className={styles.red}>
              <div className={styles.podaci}>
                <span className={styles.datum}>
                  {formatDatum(izvestaj.datumIzvestaja)}
                </span>
                {izvestaj.autor && (
                  <span className={styles.autor} title={izvestaj.autor}>
                    {izvestaj.autor}
                  </span>
                )}
              </div>
              <div className={styles.akcije}>
                {['SRB', 'ENG'].map((jezik) => (
                  <button
                    key={jezik}
                    type="button"
                    className={styles.dugmeJezik}
                    onClick={() => otvoriPregled(izvestaj, jezik)}
                  >
                    {jezik}
                  </button>
                ))}
                {jeInmold && (
                  <>
                    <Switch
                      size="small"
                      checked={Boolean(izvestaj.vidljivoKlijentu)}
                      title={t('projectReports.visibleToClient')}
                      onChange={(e) =>
                        izmeniVidljivost(izvestaj, e.target.checked)
                      }
                      inputProps={{
                        'aria-label': t('projectReports.visibleToClient'),
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: colors.ORANGE,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                          { backgroundColor: colors.ORANGE },
                      }}
                    />
                    <button
                      type="button"
                      className={styles.dugmeIkona}
                      title={t('projectReports.edit')}
                      onClick={() => setIzmena({ id: izvestaj.id })}
                    >
                      <i className="fas fa-pencil-alt" />
                    </button>
                    <button
                      type="button"
                      className={styles.dugmeIkona}
                      title={t('projectReports.delete')}
                      onClick={() => setZaBrisanje(izvestaj)}
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {izmena && (
        <IzmenaIzvestajaProjektaDialog
          open
          idIzvestaja={izmena.id}
          idMasine={idMasine}
          onClose={() => setIzmena(null)}
          onSacuvano={() => {
            setIzmena(null);
            ucitaj();
          }}
        />
      )}

      <MuiDialog
        maxWidth="xs"
        open={Boolean(zaBrisanje)}
        handleClose={() => setZaBrisanje(null)}
        handleConfirm={obrisi}
        title={t('projectReports.delete')}
        text={t('projectReports.deleteConfirm')}
      />

      <Dialog
        open={Boolean(pregled)}
        onClose={() => setPregled(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '94vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>{pregled?.naslov}</DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, display: 'flex', overflow: 'hidden' }}
        >
          {pregled && (
            <iframe
              title={pregled.naslov}
              src={pregled.url}
              style={{ flex: 1, width: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
        <div className={styles.dnoPregleda}>
          <OButton
            text={t('close')}
            startIcon={icons.DIALOG_CLOSE}
            onClick={() => setPregled(null)}
            style={{
              color: colors.ORANGE,
              backgroundColor: colors.WHITE,
              border: `1px solid ${colors.ORANGE}`,
            }}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default IzvestajiPanel;
